import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { API_CONFIG } from '../../api/config';
import {
  ApiKeyCreateResponse,
  ApiKeyRecord,
  CLI_FULL_PROJECT_WORKFLOW_SCOPES,
  createApiKey,
  createServiceAccount,
  DEFAULT_API_KEY_SCOPES,
  deleteServiceAccount,
  listApiKeys,
  listServiceAccounts,
  revokeApiKey,
  ServiceAccount,
} from '../../api/serviceAccounts';
import {
  outlineActionButtonSx,
  primaryActionButtonSx,
  readableDisabledButtonSx,
} from '../../utils/actionButtonStyles';
import { formatApiError } from '../../utils/formatApiError';
import ActionButton from '../common/ActionButton';

type Props = {
  projectId: string;
  canManage: boolean;
};

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return 'Never';
  }

  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const ApiAccessTab: React.FC<Props> = ({ projectId, canManage }) => {
  const [serviceAccounts, setServiceAccounts] = useState<ServiceAccount[]>([]);
  const [apiKeysByAccount, setApiKeysByAccount] = useState<Record<string, ApiKeyRecord[]>>({});
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [serviceAccountName, setServiceAccountName] = useState('');
  const [serviceAccountDescription, setServiceAccountDescription] = useState('');
  const [apiKeyName, setApiKeyName] = useState('');
  const [scopes, setScopes] = useState<string[]>(CLI_FULL_PROJECT_WORKFLOW_SCOPES);
  const [createdKey, setCreatedKey] = useState<ApiKeyCreateResponse | null>(null);

  const selectedAccount = useMemo(
    () => serviceAccounts.find((account) => account.id === selectedAccountId) || null,
    [serviceAccounts, selectedAccountId]
  );

  const loadServiceAccounts = React.useCallback(async () => {
    if (!canManage || !projectId) {
      setServiceAccounts([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const accounts = await listServiceAccounts(projectId);
      setServiceAccounts(accounts);
      setSelectedAccountId((current) => {
        if (current && accounts.some((account) => account.id === current)) {
          return current;
        }
        return accounts[0]?.id || null;
      });
    } catch (err) {
      setError(formatApiError(err, 'Failed to load API access configuration'));
    } finally {
      setLoading(false);
    }
  }, [canManage, projectId]);

  const loadApiKeys = React.useCallback(
    async (serviceAccountId: string) => {
      try {
        const keys = await listApiKeys(serviceAccountId);
        setApiKeysByAccount((current) => ({ ...current, [serviceAccountId]: keys }));
      } catch (err) {
        setError(formatApiError(err, 'Failed to load API keys'));
      }
    },
    []
  );

  useEffect(() => {
    void loadServiceAccounts();
  }, [loadServiceAccounts]);

  useEffect(() => {
    if (selectedAccountId) {
      void loadApiKeys(selectedAccountId);
    }
  }, [selectedAccountId, loadApiKeys]);

  const toggleScope = (scope: string) => {
    setScopes((current) =>
      current.includes(scope)
        ? current.filter((item) => item !== scope)
        : [...current, scope]
    );
  };

  const handleCreateServiceAccount = async () => {
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const created = await createServiceAccount({
        project_id: projectId,
        name: serviceAccountName.trim(),
        description: serviceAccountDescription.trim() || undefined,
      });
      setServiceAccountName('');
      setServiceAccountDescription('');
      setSuccess('Service account created.');
      await loadServiceAccounts();
      setSelectedAccountId(created.id);
    } catch (err) {
      setError(formatApiError(err, 'Failed to create service account'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateApiKey = async () => {
    if (!selectedAccountId) {
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const created = await createApiKey(selectedAccountId, {
        name: apiKeyName.trim() || undefined,
        scopes,
      });
      setCreatedKey(created);
      setApiKeyName('');
      setSuccess('API key created. Copy it now.');
      await loadApiKeys(selectedAccountId);
    } catch (err) {
      setError(formatApiError(err, 'Failed to create API key'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevokeApiKey = async (apiKeyId: string) => {
    if (!selectedAccountId) {
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await revokeApiKey(apiKeyId);
      setSuccess('API key revoked.');
      await loadApiKeys(selectedAccountId);
    } catch (err) {
      setError(formatApiError(err, 'Failed to revoke API key'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisableServiceAccount = async (serviceAccountId: string) => {
    setSubmitting(true);
    setError(null);
    try {
      await deleteServiceAccount(serviceAccountId);
      setSuccess('Service account disabled.');
      await loadServiceAccounts();
    } catch (err) {
      setError(formatApiError(err, 'Failed to disable service account'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!canManage) {
    return null;
  }

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h6" fontWeight={600}>
          Service Accounts & API Keys
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.7 }}>
          Use user login for the web app. Use service accounts and API keys for CI/CD and other non-human integrations.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card className="enterprise-card">
        <CardContent>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Service Accounts
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                Create project-scoped credentials for pipelines, bots, and deployment automation.
              </Typography>
            </Box>

            <Stack spacing={2} direction={{ xs: 'column', md: 'row' }}>
              <TextField
                label="Name"
                value={serviceAccountName}
                onChange={(event) => setServiceAccountName(event.target.value)}
                fullWidth
                size="small"
                placeholder="ci-functional-selection"
              />
              <TextField
                label="Description"
                value={serviceAccountDescription}
                onChange={(event) => setServiceAccountDescription(event.target.value)}
                fullWidth
                size="small"
                placeholder="GitHub Actions workflow for checkout service"
              />
              <ActionButton
                disabled={submitting || !serviceAccountName.trim()}
                onClick={() => void handleCreateServiceAccount()}
                sx={{
                  minWidth: 170,
                  ...primaryActionButtonSx,
                }}
              >
                Create service account
              </ActionButton>
            </Stack>

            <Stack spacing={1.5}>
              {loading ? (
                <Typography color="text.secondary">Loading service accounts…</Typography>
              ) : serviceAccounts.length === 0 ? (
                <Alert
                  severity="info"
                  sx={{
                    bgcolor: 'rgba(103, 217, 255, 0.08)',
                    border: '1px solid rgba(103, 217, 255, 0.18)',
                  }}
                >
                  No service accounts yet. Create one for your current project to issue CI/CD API keys.
                </Alert>
              ) : (
                serviceAccounts.map((account) => (
                  <Card
                    key={account.id}
                    sx={{
                      borderRadius: 3,
                      border: account.id === selectedAccountId
                        ? '1px solid rgba(57,255,20,0.28)'
                        : '1px solid rgba(255,255,255,0.08)',
                      bgcolor: 'rgba(255,255,255,0.02)',
                    }}
                  >
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Stack
                          direction={{ xs: 'column', md: 'row' }}
                          justifyContent="space-between"
                          alignItems={{ xs: 'flex-start', md: 'center' }}
                          spacing={1.5}
                        >
                          <Box>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                              <Typography variant="subtitle1" fontWeight={700}>
                                {account.name}
                              </Typography>
                              <Chip
                                size="small"
                                label={account.is_active ? 'Active' : 'Disabled'}
                                color={account.is_active ? 'success' : 'default'}
                                variant="outlined"
                              />
                            </Stack>
                            {account.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                                {account.description}
                              </Typography>
                            )}
                          </Box>

                          <Stack direction="row" spacing={1}>
                            <ActionButton
                              onClick={() => setSelectedAccountId(account.id)}
                              actionVariant={account.id === selectedAccountId ? 'active' : 'default'}
                              sx={{
                                ...(account.id === selectedAccountId ? primaryActionButtonSx : outlineActionButtonSx),
                              }}
                            >
                              Manage keys
                            </ActionButton>
                            <ActionButton
                              disabled={submitting || !account.is_active}
                              onClick={() => void handleDisableServiceAccount(account.id)}
                              sx={readableDisabledButtonSx}
                            >
                              Disable
                            </ActionButton>
                          </Stack>
                        </Stack>

                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                          <Typography variant="caption" color="text.secondary">
                            Created: {formatDateTime(account.created_at)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Last used: {formatDateTime(account.last_used_at)}
                          </Typography>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card className="enterprise-card">
        <CardContent>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                API Keys
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                {selectedAccount
                  ? `Create project-scoped keys for ${selectedAccount.name}. Raw keys are shown once and never stored in plain text.`
                  : 'Select a service account to issue and review API keys.'}
              </Typography>
            </Box>

            <Stack spacing={2} direction={{ xs: 'column', md: 'row' }}>
              <TextField
                label="Key name"
                value={apiKeyName}
                onChange={(event) => setApiKeyName(event.target.value)}
                size="small"
                fullWidth
                disabled={!selectedAccount || submitting}
                placeholder="github-actions-key"
              />
            </Stack>

            <Stack spacing={1}>
              <ActionButton
                disabled={!selectedAccount || submitting}
                onClick={() => setScopes(CLI_FULL_PROJECT_WORKFLOW_SCOPES)}
                sx={{ ...outlineActionButtonSx, alignSelf: 'flex-start' }}
              >
                CLI full project workflow
              </ActionButton>
              <Typography variant="body2" color="text.secondary">
                Project-scoped key for normal CLI use. It cannot create projects or access other projects.
              </Typography>
            </Stack>

            <FormGroup row>
              {DEFAULT_API_KEY_SCOPES.map((scope) => (
                <FormControlLabel
                  key={scope}
                  control={
                    <Checkbox
                      checked={scopes.includes(scope)}
                      onChange={() => toggleScope(scope)}
                      disabled={!selectedAccount || submitting}
                    />
                  }
                  label={scope}
                />
              ))}
            </FormGroup>

            <ActionButton
              disabled={!selectedAccount || submitting || scopes.length === 0}
              onClick={() => void handleCreateApiKey()}
              sx={{
                alignSelf: 'flex-start',
                ...primaryActionButtonSx,
              }}
            >
              Create API key
            </ActionButton>

            <Divider />

            <Stack spacing={1.5}>
              {(selectedAccountId ? apiKeysByAccount[selectedAccountId] || [] : []).map((key) => (
                <Card
                  key={key.id}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid rgba(255,255,255,0.08)',
                    bgcolor: 'rgba(255,255,255,0.02)',
                  }}
                >
                  <CardContent>
                    <Stack spacing={1.25}>
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                        spacing={1}
                      >
                        <Box>
                          <Typography fontWeight={700}>{key.key_prefix}</Typography>
                          {key.name && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {key.name}
                            </Typography>
                          )}
                        </Box>
                        <Stack direction="row" spacing={1}>
                          <Chip
                            size="small"
                            label={key.revoked_at ? 'Revoked' : key.is_active ? 'Active' : 'Inactive'}
                            color={key.revoked_at ? 'default' : key.is_active ? 'success' : 'warning'}
                            variant="outlined"
                          />
                          {!key.revoked_at && key.is_active && (
                            <ActionButton
                              disabled={submitting}
                              onClick={() => void handleRevokeApiKey(key.id)}
                              sx={readableDisabledButtonSx}
                            >
                              Revoke
                            </ActionButton>
                          )}
                        </Stack>
                      </Stack>

                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {key.scopes.map((scope) => (
                          <Chip key={scope} size="small" label={scope} variant="outlined" />
                        ))}
                      </Stack>

                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <Typography variant="caption" color="text.secondary">
                          Created: {formatDateTime(key.created_at)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Last used: {formatDateTime(key.last_used_at)}
                        </Typography>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card className="enterprise-card">
        <CardContent>
          <Stack spacing={1.5}>
            <Typography variant="subtitle1" fontWeight={700}>
              API quickstart
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Use user login for the UI. Use API keys for CI/CD automation.
            </Typography>
            <Box
              component="pre"
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.04)',
                overflowX: 'auto',
                fontSize: '0.82rem',
                m: 0,
              }}
            >
{`curl -X GET "${API_CONFIG.BASE_URL}/projects/" \\
  -H "Authorization: Bearer ${createdKey?.api_key || 'qm_dev_xxx'}"

curl -X GET "${API_CONFIG.BASE_URL}/functional/select?project_id=${projectId || '<PROJECT_ID>'}" \\
  -H "Authorization: Bearer ${createdKey?.api_key || 'qm_dev_xxx'}"`}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={!!createdKey} onClose={() => setCreatedKey(null)} maxWidth="sm" fullWidth>
        <DialogTitle>API key created</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="warning">
              Copy this key now. You will not be able to see it again.
            </Alert>
            <TextField value={createdKey?.api_key || ''} fullWidth InputProps={{ readOnly: true }} />
            <Typography variant="body2" color="text.secondary">
              {createdKey?.warning}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <ActionButton
            onClick={async () => {
              if (createdKey?.api_key && navigator?.clipboard) {
                await navigator.clipboard.writeText(createdKey.api_key);
              }
            }}
          >
            Copy key
          </ActionButton>
          <ActionButton onClick={() => setCreatedKey(null)}>
            Done
          </ActionButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ApiAccessTab;
